/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TreeView from '@material-ui/lab/TreeView';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TreeItem from '@material-ui/lab/TreeItem';
import { ParentEntityRef } from './useCatalogHierarchy';
import { Card, Typography } from '@material-ui/core';
import { useEntityList } from '@backstage/plugin-catalog-react';
import { CustomFilters } from '../filters/types';
import { EntityParentFilter } from '../filters/EntityParentFilter';
import { isArray } from 'lodash';
import { stringifyEntityRef } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    flexGrow: 1,
    maxWidth: 400,
    padding: theme.spacing(1),
  },
  treeItem: {
    lineHeight: 2,
  },
}));

const stringifyParentEntityRef = (entity: ParentEntityRef) => {
  return stringifyEntityRef({
    kind: entity.kind,
    name: entity.name,
    namespace: entity.namespace,
  });
};

export default function SystemTreeNav({
  entities,
  defaultExpanded = [],
  defaultSelected = [],
}: {
  entities: ParentEntityRef[];
  defaultExpanded?: string[];
  defaultSelected?: string[];
}) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState<string[]>(defaultExpanded);
  const {
    updateFilters,
    filters,
    queryParameters: { parents: parentsParameter },
  } = useEntityList<CustomFilters>();

  const queryParamParents = useMemo(
    () => [parentsParameter].flat().filter(Boolean) as string[],
    [parentsParameter],
  );

  const [selectedParents, setSelectedParents] = useState(
    queryParamParents.length
      ? queryParamParents
      : filters.parents?.values ?? defaultSelected,
  );
  // Set selected parents on query parameter updates; this happens at initial page load and from
  // external updates to the page location.
  useEffect(() => {
    if (queryParamParents.length) {
      const filter = new EntityParentFilter(queryParamParents);
      setSelectedParents(filter.values);
    }
  }, [queryParamParents]);

  useEffect(() => {
    updateFilters({
      parents: selectedParents.length
        ? new EntityParentFilter(selectedParents)
        : undefined,
    });
  }, [selectedParents, updateFilters]);

  useEffect(() => {
    if (entities && entities.length > 0) {
      const firstChild = entities[0].children?.[0];
      const parentEntityRef = stringifyParentEntityRef(entities[0]);
      if (firstChild) {
        setExpanded([`parent_${parentEntityRef}`]);
        setSelectedParents([stringifyParentEntityRef(firstChild)]);
      } else {
        setSelectedParents([parentEntityRef]);
      }
    }
  }, [entities]);

  const renderTree = (nodes: ParentEntityRef) => {
    const isParent = nodes.children && nodes.children.length > 0;
    let id = stringifyEntityRef({
      kind: nodes.kind,
      name: nodes.name,
      namespace: nodes.namespace,
    });
    id = isParent ? `parent_${id}` : id;
    return (
      <TreeItem
        key={id}
        nodeId={id}
        label={
          <Typography variant="body1" className={classes.treeItem}>
            {nodes.title || nodes.name}
          </Typography>
        }
      >
        {isParent ? nodes.children?.map(node => renderTree(node)) : null}
      </TreeItem>
    );
  };

  const handleToggle = (_event: React.ChangeEvent<{}>, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  const handleSelect = (
    _event: React.ChangeEvent<{}>,
    nodeIds: string | string[],
  ) => {
    const leafNodeIds = (isArray(nodeIds) ? nodeIds : [nodeIds]).filter(
      nodeId => !nodeId.startsWith('parent_'),
    );
    if (leafNodeIds && leafNodeIds.length > 0) {
      setSelectedParents(leafNodeIds);
    }
  };

  return (
    <Card className={classes.root}>
      <TreeView
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpanded={['root']}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        selected={selectedParents}
        onNodeToggle={handleToggle}
        onNodeSelect={handleSelect}
      >
        {entities.map(entity => renderTree(entity))}
      </TreeView>
    </Card>
  );
}
